import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Lock, User, ArrowRight } from "lucide-react";

export default function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    const res = await register(email.trim(), password, firstName.trim());
    setBusy(false);
    if (res.ok) navigate("/community", { replace: true });
    else setErr(res.error);
  };

  return (
    <div data-testid="page-signup" className="py-20 lg:py-28">
      <div className="max-w-md mx-auto px-6">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
          <span className="w-6 h-px bg-terracotta" />
          Join the community
        </span>
        <h1 className="font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          Make a quiet little <em className="text-terracotta">account</em>.
        </h1>
        <p className="mt-4 text-base text-charcoal-light dark:text-bone/65 leading-relaxed">
          Only your first name is shown to others. No surnames, no display photos.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="signup-form">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/60">First name</span>
            <div className="mt-2 relative">
              <User size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-light dark:text-bone/50" />
              <input
                type="text"
                required
                maxLength={40}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="signup-firstname"
                placeholder="What should others call you?"
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-bone-200 dark:border-charcoal-light/30 bg-bone dark:bg-charcoal-light/10 text-charcoal dark:text-bone outline-none focus:border-terracotta transition placeholder:text-charcoal-light/60"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/60">Email</span>
            <div className="mt-2 relative">
              <Mail size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-light dark:text-bone/50" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email"
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-bone-200 dark:border-charcoal-light/30 bg-bone dark:bg-charcoal-light/10 text-charcoal dark:text-bone outline-none focus:border-terracotta transition"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/60">Password</span>
            <div className="mt-2 relative">
              <Lock size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-light dark:text-bone/50" />
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password"
                placeholder="At least 8 characters"
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-bone-200 dark:border-charcoal-light/30 bg-bone dark:bg-charcoal-light/10 text-charcoal dark:text-bone outline-none focus:border-terracotta transition placeholder:text-charcoal-light/60"
              />
            </div>
          </label>

          {err && (
            <p data-testid="signup-error" className="text-sm text-terracotta">
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="signup-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-terracotta text-bone text-sm font-medium tracking-wide hover:bg-terracotta-hover transition disabled:opacity-60"
          >
            {busy ? "Creating…" : "Create account"}
            {!busy && <ArrowRight size={16} strokeWidth={1.5} />}
          </button>
        </form>

        <p className="mt-8 text-sm text-charcoal-light dark:text-bone/65 text-center">
          Already have one?{" "}
          <Link to="/signin" data-testid="signup-to-signin" className="text-terracotta hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
